import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import { api } from '../lib/api';

function slugify(s) {
  return (s || '').toLowerCase()
    .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
}

export default function EventDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.publicContent('events').then(list => {
      if (cancel) return;
      const all = (list || []).map(it => ({ id: it.id, ...(it.data || {}) }));
      const found = all.find(x => x.slug === slug || slugify(x.title) === slug);
      setItem(found || null);
      setLoading(false);
    }).catch(() => { setItem(null); setLoading(false); });
    return () => { cancel = true; };
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-ink-400">Yükleniyor...</div>;
  if (!item) return (
    <div className="min-h-[60vh] grid place-items-center">
      <div className="text-center">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">404</div>
        <h1 className="font-display text-4xl mt-3">Etkinlik bulunamadı</h1>
        <Link to="/projeler?tab=etkinlik" className="mt-5 inline-flex items-center gap-2 text-ink-500 hover:text-ink-600"><ArrowLeft className="w-4 h-4" /> Etkinliklere dön</Link>
      </div>
    </div>
  );

  return (
    <>
      <section className="relative bg-ink-600 text-cream overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #C9A96E 0, transparent 55%), radial-gradient(circle at 80% 70%, #E8826F 0, transparent 55%)' }} />
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 pt-16 pb-20 md:pt-20 md:pb-28">
          <Link to="/projeler?tab=etkinlik" className="inline-flex items-center gap-1.5 text-cream/60 hover:text-gold text-xs tracking-[0.25em] uppercase font-semibold transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Etkinlikler
          </Link>
          <div className="mt-6 flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase">
            {item.category && <span className="text-gold"><Tag className="w-3 h-3 inline mr-1" />{item.category}</span>}
            {item.date && <span className="text-cream/70"><Calendar className="w-3 h-3 inline mr-1" />{item.date}</span>}
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-5 leading-[1.05]">{item.title}</h1>
          {item.excerpt && <p className="mt-6 text-cream/75 text-base md:text-lg max-w-3xl">{item.excerpt}</p>}
        </div>
      </section>

      {item.image && (
        <div className="bg-cream">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-10 -mt-14 md:-mt-20 relative">
            <div className="rounded-3xl overflow-hidden border border-ink-100 shadow-2xl shadow-ink-900/15">
              <img src={item.image} alt={item.title} className="w-full max-h-[520px] object-cover" />
            </div>
          </div>
        </div>
      )}

      <section className="bg-cream">
        <div className="max-w-[800px] mx-auto px-6 lg:px-10 py-16 md:py-24">
          {item.content ? (
            <div className="richtext" dangerouslySetInnerHTML={{ __html: item.content }} />
          ) : (
            <p className="text-ink-500 text-lg leading-relaxed">{item.excerpt}</p>
          )}

          <div className="mt-16 pt-10 border-t border-ink-100 flex flex-wrap items-center justify-between gap-4">
            <Link to="/projeler?tab=etkinlik" className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-600 text-sm font-semibold">
              <ArrowLeft className="w-4 h-4" /> Tüm etkinlikler
            </Link>
            <Link to="/gonullu" className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-6 h-12 font-semibold hover:bg-gold-400 transition-colors">
              Gönüllü ol →
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .richtext{font-size:17px;line-height:1.75;color:#1A2744}
        .richtext h1{font-family:'Playfair Display',Georgia,serif;font-size:2.4rem;line-height:1.15;margin:1.4em 0 0.5em;color:#0F1E4D}
        .richtext h2{font-family:'Playfair Display',Georgia,serif;font-size:1.85rem;line-height:1.2;margin:1.3em 0 0.4em;color:#0F1E4D}
        .richtext h3{font-family:'Playfair Display',Georgia,serif;font-size:1.4rem;margin:1em 0 0.3em;color:#0F1E4D}
        .richtext p{margin:0.9em 0}
        .richtext ul,.richtext ol{padding-left:1.6em;margin:0.9em 0}
        .richtext ul li{list-style:disc;margin:0.35em 0}
        .richtext ol li{list-style:decimal;margin:0.35em 0}
        .richtext blockquote{border-left:3px solid #C9A96E;padding-left:1.25em;color:#374060;font-style:italic;margin:1.3em 0;font-size:1.18em}
        .richtext a{color:#0F1E4D;text-decoration:underline;text-underline-offset:3px}
        .richtext a:hover{color:#C9A96E}
        .richtext img{border-radius:0.75rem;max-width:100%;margin:1.4em 0}
        .richtext hr{border:0;border-top:1px solid #E8DECF;margin:1.8em 0}
        .richtext code{background:#F4EEE2;padding:0.15em 0.4em;border-radius:4px;font-size:0.9em}
      `}</style>
    </>
  );
}
